import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import { Phone, Mail, Clock, MapPin, MessageCircle } from 'lucide-react';
import Postuler from './Postuler';
import Connexion from './Connexion';
import Dashboard from './Dashboard';
import AdminApp from './AdminApp';
import logo from './safi1.jpeg';
import heroBg from './safi2.avif';
import safi3 from './safi3.webp';
import safi4 from './safi4.webp';
import safi5 from './safi5.jpg';
import safi6 from './safi6.jpeg';
import safi7 from './safi7.jpg';
import safi8 from './safi8.jpg';
import safi9 from './safi9.webp';

const slideData = [
  {
    img: safi3,
    icon: '🖥️',
    tag: 'Formation en ligne',
    title: 'Apprenez l\'anglais depuis chez vous',
    desc: 'Nos cours en ligne sont accessibles depuis le Sénégal, le Gabon, le Cameroun et le Congo Brazza. Avec des formateurs qualifiés disponibles en visioconférence, vous progressez à votre propre rythme sans sacrifier votre quotidien.',
    points: ['Cours en direct ou en replay', 'Suivi personnalisé par un formateur', 'Accessible depuis 4 pays'],
  },
  {
    img: safi4,
    icon: '🏫',
    tag: 'Cours en présentiel',
    title: 'Une immersion totale au Sénégal',
    desc: 'Nos salles de cours au Sénégal offrent une expérience d\'apprentissage immersive et interactive. Travailler directement avec vos formateurs en face à face accélère votre progression et renforce votre confiance à l\'oral.',
    points: ['Petits groupes pour plus d\'attention', 'Exercices de conversation intensive', 'Disponible à Dakar, Sénégal'],
  },
  {
    img: safi5,
    icon: '✈️',
    tag: 'Partir à l\'international',
    title: 'Votre tremplin vers le monde',
    desc: 'Après votre formation, SAFI Academia vous accompagne dans toutes vos démarches pour partir en Angleterre, aux États-Unis ou en Afrique du Sud. Visa, dossier d\'admission, hébergement — notre équipe est à vos côtés à chaque étape.',
    points: ['Accompagnement visa & procédures', 'Partenariats avec des établissements à l\'étranger', 'Suivi jusqu\'à votre arrivée'],
  },
];

function ProgramSlideshow() {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((index) => {
    if (index === active) return;
    setFading(true);
    setTimeout(() => {
      setActive(index);
      setFading(false);
    }, 450);
  }, [active]);

  useEffect(() => {
    const timer = setTimeout(() => {
      goTo((active + 1) % slideData.length);
    }, 45000);
    return () => clearTimeout(timer);
  }, [active, goTo]);

  const slide = slideData[active];

  return (
    <div className="slideshow">
      <div className={`slideshow__inner ${fading ? 'slideshow__inner--fading' : ''}`}>
        {/* IMAGE GAUCHE */}
        <div className="slideshow__image-wrap">
          <img src={slide.img} alt={slide.title} className="slideshow__img" />
        </div>

        {/* CONTENU DROITE — même état, change en même temps */}
        <div className="slideshow__content">
          <h3 className="slideshow__title">{slide.title}</h3>
          <p className="slideshow__desc">{slide.desc}</p>
          <ul className="slideshow__points">
            {slide.points.map((pt, i) => (
              <li key={i}><span className="slideshow__check">✓</span>{pt}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* DOTS */}
      <div className="slideshow__dots">
        {slideData.map((_, i) => (
          <button
            key={i}
            className={`slideshow__dot ${active === i ? 'slideshow__dot--active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}


const destSlides = [
  {
    img: safi6,
    flag: 'https://flagcdn.com/w40/gb.png',
    country: 'Angleterre',
    eyebrow: '🇬🇧 Royaume-Uni',
    title: 'Étudier & vivre en Angleterre',
    desc: 'Études, travail ou visa — notre équipe gère toutes vos démarches pour vous installer au Royaume-Uni après votre formation.',
  },
  {
    img: safi7,
    flag: 'https://flagcdn.com/w40/us.png',
    country: 'États-Unis',
    eyebrow: '🇺🇸 États-Unis',
    title: 'Partir aux États-Unis',
    desc: 'Visa étudiant, visa travail — SAFI Academia vous accompagne de A à Z pour réaliser votre rêve américain.',
  },
  {
    img: safi8,
    flag: 'https://flagcdn.com/w40/za.png',
    country: 'Afrique du Sud',
    eyebrow: '🇿🇦 Afrique du Sud',
    title: 'S\'installer en Afrique du Sud',
    desc: 'Un tremplin accessible et stimulant pour débuter votre parcours international, à quelques heures de vol seulement.',
  },
];

function DestinationSlider() {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const total = destSlides.length;

  const goTo = useCallback((index) => {
    const next = (index + total) % total;
    setFading(true);
    setTimeout(() => {
      setActive(next);
      setFading(false);
    }, 450);
  }, [total]);

  const slide = destSlides[active];

  return (
    <div className="dest-slider">
      {/* Background image */}
      <div className={`dest-slider__bg ${fading ? 'dest-slider__bg--fading' : ''}`}>
        <img src={slide.img} alt={slide.country} className="dest-slider__img" />
        <div className="dest-slider__overlay" />
      </div>

      {/* Text content */}
      <div className={`dest-slider__content ${fading ? 'dest-slider__content--fading' : ''}`}>
        <p className="dest-slider__eyebrow">{slide.eyebrow}</p>
        <h3 className="dest-slider__title">{slide.title}</h3>
        <p className="dest-slider__desc">{slide.desc}</p>
        <div className="dest-slider__dots">
          {destSlides.map((_, i) => (
            <button
              key={i}
              className={`dest-slider__dot ${active === i ? 'dest-slider__dot--active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </div>

      {/* Arrow left */}
      <button className="dest-slider__arrow dest-slider__arrow--left" onClick={() => goTo(active - 1)} aria-label="Précédent">
        ‹
      </button>

      {/* Arrow right */}
      <button className="dest-slider__arrow dest-slider__arrow--right" onClick={() => goTo(active + 1)} aria-label="Suivant">
        ›
      </button>
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { number: '4', label: 'Pays présents — Sénégal, Gabon, Cameroun, Congo Brazza' },
    { number: '3', label: 'Destinations à l\'international — UK, USA, Afrique du Sud' },
    { number: '2', label: 'Modes de formation — En ligne & En présentiel' },
    { number: '100%', label: 'Accompagnement personnalisé' },
  ];

  const programs = [
    {
      icon: '🖥️',
      title: 'Cours d\'anglais en ligne',
      desc: 'Apprenez l\'anglais depuis chez vous, à votre rythme, avec des formateurs qualifiés. Nos cours en ligne sont accessibles depuis le Sénégal, le Gabon, le Cameroun et le Congo Brazza.',
      link: '#',
    },
    {
      icon: '🏫',
      title: 'Cours en présentiel — Sénégal',
      desc: 'Bénéficiez d\'une immersion totale dans nos salles de cours au Sénégal. Des sessions interactives pour progresser plus vite avec un suivi direct de vos formateurs.',
      link: '#',
    },
    {
      icon: '✈️',
      title: 'Partir à l\'international',
      desc: 'Après votre formation, SAFI Academia vous accompagne dans toutes les démarches pour étudier ou vivre en Angleterre, aux États-Unis ou en Afrique du Sud.',
      link: '#',
    },
  ];

  const steps = [
    { num: '01', title: 'Choisissez votre format', desc: 'Cours en ligne depuis votre pays ou en présentiel au Sénégal — choisissez ce qui vous convient.' },
    { num: '02', title: 'Suivez votre formation', desc: 'Progressez en anglais avec nos formateurs expérimentés, à votre rythme et selon vos objectifs.' },
    { num: '03', title: 'Validez votre niveau', desc: 'Obtenez une certification reconnue qui atteste de votre maîtrise de l\'anglais.' },
    { num: '04', title: 'Partez à l\'international', desc: 'Notre équipe vous guide pas à pas dans les démarches pour partir en UK, aux USA ou en Afrique du Sud.' },
  ];

  const faqs = [
    {
      q: 'Dans quels pays SAFI Academia est-elle disponible ?',
      a: 'SAFI Academia est présente au Sénégal, au Gabon, au Cameroun et au Congo Brazza. Les cours en présentiel se déroulent uniquement au Sénégal pour le moment. Les cours en ligne sont accessibles depuis tous ces pays.',
    },
    {
      q: 'Peut-on suivre les cours entièrement en ligne ?',
      a: 'Oui ! Nos cours d\'anglais sont disponibles 100% en ligne. Vous pouvez apprendre depuis chez vous, à votre rythme, avec un suivi personnalisé par nos formateurs.',
    },
    {
      q: 'SAFI Academia aide-t-elle à partir à l\'étranger ?',
      a: 'Absolument. Après votre formation, notre équipe vous accompagne dans toutes les démarches administratives pour partir étudier ou vous installer en Angleterre, aux États-Unis ou en Afrique du Sud.',
    },
    {
      q: 'Faut-il déjà parler anglais pour s\'inscrire ?',
      a: 'Non ! Nous accueillons tous les niveaux, des grands débutants aux apprenants avancés. Un test de positionnement est réalisé à l\'inscription pour vous placer dans le groupe adapté.',
    },
    {
      q: 'Comment se fait le paiement ?',
      a: 'Pour les étudiants au Sénégal, le paiement s\'effectue directement dans nos bureaux SAFI Academia. Si vous êtes dans un autre pays ou si vous ne pouvez pas vous déplacer, notre équipe du service client vous expliquera en détail les modalités de paiement disponibles selon votre situation. N\'hésitez pas à nous contacter.',
    },
    {
      q: 'Je suis au Gabon, au Cameroun ou au Congo — comment faire ?',
      a: 'Pour les pays où SAFI Academia ne dispose pas encore de bureaux physiques (Gabon, Cameroun, Congo Brazza), tous les cours se font en ligne. Pour l\'inscription et le paiement, rapprochez-vous directement de notre service client qui vous accompagnera étape par étape.',
    },
  ];

  const [openFaq, setOpenFaq] = useState(null);
  const [niveauOpen, setNiveauOpen] = useState(false);

  return (
    <div className="app">
      {/* NAVBAR */}
      <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__container">
          <div className="navbar__brand">
            <img src={logo} alt="SAFI Academia" className="navbar__logo" />
            <span className="navbar__name">SAFI Academia</span>
          </div>
          <nav className={`navbar__links ${menuOpen ? 'navbar__links--open' : ''}`}>
            <a href="#programmes" onClick={() => setMenuOpen(false)}>Programmes</a>
            <a href="#admissions" onClick={() => setMenuOpen(false)}>Admissions</a>
            <a href="#niveaux" onClick={() => setMenuOpen(false)}>Niveaux</a>
            <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
          </nav>
          <div className="navbar__actions">
            <a href="/connexion" className="btn btn--ghost">Se connecter</a>
            <a href="/postuler" className="btn btn--gold">Postuler</a>
          </div>
          <button className="navbar__burger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero__overlay" />
        <div className="hero__content">
          <p className="hero__eyebrow">✦ Stand up and Act ✦</p>
          <h1 className="hero__title">
            Maîtrisez l'anglais.<br />Ouvrez le monde.
          </h1>
          <p className="hero__subtitle">
            SAFI Academia vous forme à l'anglais en ligne ou en présentiel au Sénégal,
            et vous accompagne jusqu'à votre départ en Angleterre, aux États-Unis ou en Afrique du Sud.
          </p>
          <div className="hero__buttons">
            <a href="/postuler" className="btn btn--gold btn--lg">Postuler maintenant</a>
            <a href="#programmes" className="btn btn--outline btn--lg">Découvrir nos programmes</a>
          </div>
        </div>
        <div className="hero__scroll-indicator">
          <span>↓</span>
        </div>
      </section>

      {/* STATS */}
      <section className="stats">
        <div className="stats__container">
          {stats.map((s, i) => (
            <div className="stats__item" key={i}>
              <span className="stats__number">{s.number}</span>
              <span className="stats__label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMMES */}
      <section className="programs" id="programmes">
        <div className="programs__header">
          <div className="programs__header-left">
            <p className="programs__eyebrow">Nos formations</p>
            <h2 className="programs__title">Une formation taillée<br />pour vos ambitions</h2>
          </div>
          <p className="programs__subtitle">
            En ligne depuis votre pays ou en présentiel au Sénégal — SAFI Academia s'adapte à votre vie
            et vous accompagne jusqu'à l'international.
          </p>
        </div>
        <ProgramSlideshow />
      </section>

      {/* QUOTE BANNER */}
      <section className="quote-banner">
        <div className="quote-banner__container">
          <blockquote>
            "L'anglais n'est pas juste une langue — c'est une clé qui ouvre des portes partout dans le monde."
          </blockquote>
          <cite>— L'équipe SAFI Academia</cite>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="steps" id="admissions">
        <div className="section-header">
          <div className="section-header__left">
            <p className="section-header__eyebrow">Comment ça marche</p>
            <h2 className="section-header__title">De votre première leçon<br />jusqu'à votre départ</h2>
          </div>
          <p className="section-header__subtitle">
            Un parcours clair et accompagné, de l'apprentissage de l'anglais à votre installation à l'international.
          </p>
        </div>
        <div className="steps__grid-wrap">
          <div className="steps__grid">
            {steps.map((s, i) => (
              <div className="step-card" key={i}>
                <span className="step-card__num">{s.num}</span>
                <h3 className="step-card__title">{s.title}</h3>
                <p className="step-card__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAYS */}
      <section className="countries">
        <div className="section-header">
          <div className="section-header__left">
            <p className="section-header__eyebrow">Notre présence</p>
            <h2 className="section-header__title">4 pays,<br />une seule mission</h2>
          </div>
          <p className="section-header__subtitle">
            SAFI Academia est implantée dans 4 pays d'Afrique. Les cours en présentiel sont disponibles au Sénégal,
            tandis que les cours en ligne sont accessibles depuis tous nos pays.
          </p>
        </div>
        <div className="countries__wrap">
          <div className="countries__grid">
            {[
              { img: 'https://flagcdn.com/w160/sn.png', name: 'Sénégal', note: 'En ligne & Présentiel' },
              { img: 'https://flagcdn.com/w160/ga.png', name: 'Gabon', note: 'En ligne' },
              { img: 'https://flagcdn.com/w160/cm.png', name: 'Cameroun', note: 'En ligne' },
              { img: 'https://flagcdn.com/w160/cg.png', name: 'Congo Brazza', note: 'En ligne' },
            ].map((c, i) => (
              <div className="country-card" key={i}>
                <div className="country-card__flag-wrap">
                  <img src={c.img} alt={c.name} className="country-card__flag-img" />
                </div>
                <h3 className="country-card__name">{c.name}</h3>
                <span className="country-card__note">{c.note}</span>
              </div>
            ))}
          </div>

          <div className="destinations">
            <div className="section-header section-header--inner">
              <div className="section-header__left">
                <p className="section-header__eyebrow">Destinations internationales</p>
                <h2 className="section-header__title">Où nous pouvons<br />vous emmener</h2>
              </div>
              <p className="section-header__subtitle">
                Après votre formation, notre équipe vous guide dans toutes les démarches pour partir étudier ou vivre à l'étranger.
              </p>
            </div>
          <DestinationSlider />
          </div>
        </div>
      </section>

      {/* NIVEAUX */}
      <section className="niveaux" id="niveaux">
        {/* Titre gros centré style edX */}
        <div className="niveaux__title-block">
          <p className="niveaux__eyebrow">Nos niveaux</p>
          <h2 className="niveaux__big-title">Trouvez le niveau<br />qui vous correspond.</h2>
          <p className="niveaux__big-sub">SAFI Academia suit le cadre européen CECR. De A1 à C2, chaque niveau est conçu pour vous faire progresser vers vos ambitions.</p>
        </div>

        {/* Bloc hero image gauche + niveaux droite */}
        <div className="niveaux__hero-wrap">
          <div className="niveaux__hero-img">
            <img src={safi9} alt="Niveaux SAFI Academia" />
            <div className="niveaux__hero-overlay" />
            <div className="niveaux__hero-badge">
              <span>A1 → C2</span>
              <p>6 niveaux, 1 objectif</p>
            </div>
          </div>

          <div className="niveaux__levels">

            <div className="niveau-group niveau-group--a">
              <div className="niveau-group__header">
                <span className="niveau-group__badge">A</span>
                <div>
                  <h3 className="niveau-group__title">Débutant</h3>
                  <p className="niveau-group__subtitle">Pour ceux qui partent de zéro ou presque</p>
                </div>
              </div>
              <div className="niveau-cards">
                <div className="niveau-card" id="niveau-a1">
                  <span className="niveau-card__code">A1</span>
                  <h4 className="niveau-card__title">Grand débutant</h4>
                  <p className="niveau-card__desc">Vous apprenez à vous présenter, comprendre des mots simples et construire vos premières phrases en anglais.</p>
                </div>
                <div className="niveau-card" id="niveau-a2">
                  <span className="niveau-card__code">A2</span>
                  <h4 className="niveau-card__title">Élémentaire</h4>
                  <p className="niveau-card__desc">Vous pouvez communiquer dans des situations courantes : faire des achats, parler de votre famille, de votre travail.</p>
                </div>
              </div>
            </div>

            <div className="niveau-group niveau-group--b">
              <div className="niveau-group__header">
                <span className="niveau-group__badge">B</span>
                <div>
                  <h3 className="niveau-group__title">Intermédiaire</h3>
                  <p className="niveau-group__subtitle">Pour ceux qui veulent gagner en aisance et fluidité</p>
                </div>
              </div>
              <div className="niveau-cards">
                <div className="niveau-card" id="niveau-b1">
                  <span className="niveau-card__code">B1</span>
                  <h4 className="niveau-card__title">Seuil</h4>
                  <p className="niveau-card__desc">Vous comprenez l'essentiel dans des situations professionnelles et personnelles. Vous voyagez sans difficultés majeures.</p>
                </div>
                <div className="niveau-card" id="niveau-b2">
                  <span className="niveau-card__code">B2</span>
                  <h4 className="niveau-card__title">Avancé</h4>
                  <p className="niveau-card__desc">Vous communiquez avec aisance, défendez votre point de vue et comprenez des textes complexes en anglais.</p>
                </div>
              </div>
            </div>

            <div className="niveau-group niveau-group--c">
              <div className="niveau-group__header">
                <span className="niveau-group__badge niveau-group__badge--gold">C</span>
                <div>
                  <h3 className="niveau-group__title">Expert <span className="niveau-group__tag">⭐ Spécialisé</span></h3>
                  <p className="niveau-group__subtitle">Réservé aux futurs Coachs et Interprètes professionnels</p>
                </div>
              </div>
              <div className="niveau-cards">
                <div className="niveau-card niveau-card--gold" id="niveau-c1">
                  <span className="niveau-card__code">C1</span>
                  <h4 className="niveau-card__title">Coach / Interprète</h4>
                  <p className="niveau-card__desc">Vous maîtrisez l'anglais avec précision. SAFI Academia peut vous recruter comme Coach pour encadrer les apprenants débutants.</p>
                </div>
                <div className="niveau-card niveau-card--gold" id="niveau-c2">
                  <span className="niveau-card__code">C2</span>
                  <h4 className="niveau-card__title">Interprète professionnel</h4>
                  <p className="niveau-card__desc">Vous atteignez la maîtrise totale. SAFI Academia peut vous employer comme Interprète pour des missions officielles.</p>
                </div>
              </div>
              <div className="niveau-recrutement">
                <span className="niveau-recrutement__icon">🎯</span>
                <p>Les étudiants niveau C peuvent être <strong>recrutés par SAFI Academia</strong> comme <strong>Coach</strong> ou <strong>Interprète</strong>.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq" id="faq">
        <div className="section-header">
          <div className="section-header__left">
            <p className="section-header__eyebrow">Questions fréquentes</p>
            <h2 className="section-header__title">Tout ce que vous<br />voulez savoir</h2>
          </div>
          <p className="section-header__subtitle">
            Une question sur nos formations, nos niveaux ou les démarches pour partir à l'étranger ? Trouvez votre réponse ici.
          </p>
        </div>
        <div className="faq__wrap">
          <div className="faq__list">
            {faqs.map((f, i) => (
              <div className={`faq__item ${openFaq === i ? 'faq__item--open' : ''}`} key={i}>
                <button className="faq__question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <span className="faq__icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <p className="faq__answer">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta" id="apply">
        <div className="cta__container">
          <img src={logo} alt="SAFI Academia" className="cta__logo" />
          <h2 className="cta__title">Votre aventure commence ici</h2>
          <p className="cta__subtitle">
            Rejoignez SAFI Academia et faites de l'anglais votre passeport vers le monde.
            Inscrivez-vous dès aujourd'hui — en ligne ou en présentiel au Sénégal.
          </p>
          <a href="/connexion" className="btn btn--gold btn--lg">Se connecter</a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">

        {/* Top bar contact rapide */}
        <div className="footer__topbar">
          <div className="footer__topbar-inner">
            <div className="footer__contact-item">
              <Phone size={16} className="footer__contact-icon" />
              <div>
                <span className="footer__contact-label">Service client</span>
                <a href="tel:+221767498675" className="footer__contact-value">+221 76 749 86 75</a>
              </div>
            </div>
            <div className="footer__topbar-divider" />
            <div className="footer__contact-item">
              <Mail size={16} className="footer__contact-icon" />
              <div>
                <span className="footer__contact-label">Email</span>
                <a href="mailto:contact@safiacademia.org" className="footer__contact-value">contact@safiacademia.org</a>
              </div>
            </div>
            <div className="footer__topbar-divider" />
            <div className="footer__contact-item">
              <Clock size={16} className="footer__contact-icon" />
              <div>
                <span className="footer__contact-label">Disponibilité</span>
                <span className="footer__contact-value">Lun – Ven · 9h15 à 17h30</span>
              </div>
            </div>
          </div>
        </div>

        {/* Corps principal */}
        <div className="footer__body">
          <div className="footer__container">

            {/* Brand */}
            <div className="footer__brand">
              <img src={logo} alt="SAFI Academia" className="footer__logo" />
              <p className="footer__tagline">* Stand up and Act *</p>
              <p className="footer__desc">
                Formez-vous à l'anglais en ligne ou en présentiel et partez à l'international avec l'accompagnement de SAFI Academia.
              </p>
              {/* Drapeaux pays */}
              <div className="footer__flags">
                <div className="footer__flag-item">
                  <img src="https://flagcdn.com/w40/sn.png" alt="Sénégal" />
                  <span>Sénégal</span>
                </div>
                <div className="footer__flag-item">
                  <img src="https://flagcdn.com/w40/ga.png" alt="Gabon" />
                  <span>Gabon</span>
                </div>
                <div className="footer__flag-item">
                  <img src="https://flagcdn.com/w40/cm.png" alt="Cameroun" />
                  <span>Cameroun</span>
                </div>
                <div className="footer__flag-item">
                  <img src="https://flagcdn.com/w40/cg.png" alt="Congo" />
                  <span>Congo</span>
                </div>
              </div>
            </div>

            {/* Liens */}
            <div className="footer__links">
              <div className="footer__col">
                <h4>Programmes</h4>
                <a href="#programmes">Cours en ligne</a>
                <a href="#programmes">Cours en présentiel</a>
                <a href="#programmes">Partir à l'international</a>
                <a href="#niveaux">Nos niveaux (A → C)</a>
              </div>
              <div className="footer__col">
                <h4>Admissions</h4>
                <a href="/postuler">Postuler</a>
                <a href="#admissions">Comment ça marche</a>
                <a href="#niveaux">Test de niveau</a>
                <a href="#faq">FAQ</a>
              </div>
              <div className="footer__col">
                <h4>Contact</h4>
                <a href="tel:+221767498675"><Phone size={13} className="footer__col-icon" /> +221 76 749 86 75</a>
                <a href="mailto:contact@safiacademia.org"><Mail size={13} className="footer__col-icon" /> contact@safiacademia.org</a>
                <a href="#"><MapPin size={13} className="footer__col-icon" /> Dakar, Sénégal</a>
                <a href="https://wa.me/221767498675" target="_blank" rel="noopener noreferrer"><MessageCircle size={13} className="footer__col-icon" /> WhatsApp</a>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer__bottom">
          <p>© 2026 SAFI Academia. Tous droits réservés.</p>
          <div className="footer__bottom-flags">
            <img src="https://flagcdn.com/w20/sn.png" alt="Sénégal" title="Sénégal" />
            <img src="https://flagcdn.com/w20/ga.png" alt="Gabon" title="Gabon" />
            <img src="https://flagcdn.com/w20/cm.png" alt="Cameroun" title="Cameroun" />
            <img src="https://flagcdn.com/w20/cg.png" alt="Congo Brazza" title="Congo Brazza" />
          </div>
        </div>

      </footer>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <Routes>
        <Route path="/"            element={<App />} />
        <Route path="/postuler"    element={<Postuler />} />
        <Route path="/connexion"   element={<Connexion />} />
        <Route path="/dashboard"   element={<Dashboard />} />
        <Route path="/admin/*"     element={<AdminApp />} />
      </Routes>
    </Router>
  );
}

export default AppWrapper;